import * as React from 'react';
import PropTypes from 'prop-types';

import Loading from './Loading';

export interface PaginationProps {
  previousText?: string;
  nextText?: string;
  maxSize?: number;
  itemsPerPage?: number;
  totalItems?: number;
  currentPage?: number;
  onChange(page: number): any;
  showLoading?: boolean;
  disabled?: boolean;
}

export interface DetermineRangeOptions {
  currentPage: number;
  totalPages: number;
  maxPages: number;
}

export interface EllipsesOptions {
  before?: number;
  after?: number;
}

const determineRange = ({
  currentPage,
  totalPages,
  maxPages,
}: DetermineRangeOptions) => {
  let startPage;
  let endPage;
  const ellipses: EllipsesOptions = { before: undefined, after: undefined };

  if (totalPages <= maxPages) {
    // Total pages are less than max, so show all pages
    startPage = 1;
    endPage = totalPages;
  } else {
    // There are more pages that can be shown, so we need to determine the range
    const maxPagesBefore = Math.floor(maxPages / 2);
    const maxPagesAfter = Math.ceil(maxPages / 2) - 1;

    if (currentPage <= maxPagesBefore) {
      // Current page is near the start
      startPage = 1;
      endPage = maxPages;
      ellipses.after = endPage + 1;
    } else if (currentPage + maxPagesAfter >= totalPages) {
      // Current page is near the end
      startPage = totalPages - maxPages + 1;
      endPage = totalPages;
      ellipses.before = startPage - 1;
    } else {
      // Current page is somewhere in the middle
      startPage = currentPage - maxPagesBefore;
      endPage = currentPage + maxPagesAfter;
      ellipses.before = startPage - 1;
      ellipses.after = endPage + 1;
    }
  }

  // Construct the range
  function* range(start: number, end: number) {
    for (let i = start; i <= end; i++) {
      yield i;
    }
  }
  const pageRange = [...range(startPage, endPage)];

  return { pageRange, ellipses };
};

/**
 * Pagination
 *
 * A pagination control component, similar to the angular.js component we've been using
 *
 * @param previousText
 * A string to use for the previous page button's label
 * @param nextText
 * A string to use for the next page button's label
 * @param maxSize
 * The upper limit of how many pages to show at a time
 * @param itemsPerPage
 * The number of items displayed per page
 * @param totalItems
 * The total number of items found
 * @param currentPage
 * The current page number (1-indexed)
 * @param onChange
 * A callback to handle page changes from interacting with this component, takes 1 argument for the new page number
 */
const Pagination = (props: PaginationProps) => {
  const {
    previousText,
    nextText,
    maxSize: maxPages,
    itemsPerPage,
    totalItems,
    currentPage,
    onChange: changePage,
    showLoading = false,
    disabled = false,
  } = props;

  const totalPages = Math.ceil(totalItems / itemsPerPage) || 1;
  // TODO: modify logic; state should not be updated in functional component render
  // this currently will potentially update state on render
  // Make sure page is within bounds
  if (changePage && currentPage < 1) changePage(1);
  if (changePage && currentPage > totalPages) changePage(totalPages);

  const { pageRange, ellipses } = determineRange({
    currentPage,
    totalPages,
    maxPages,
  });

  const handleClick = (e: React.MouseEvent, page: number) => {
    e.preventDefault();
    if (!disabled && changePage) changePage(page);
  };

  return (
    <nav aria-label="Page navigation" className="page-navigation">
      <ul className="pagination">
        <li
          key="previous"
          className={currentPage === 1 || disabled ? 'disabled' : ''}
        >
          <a
            onClick={e => {
              if (!(currentPage === 1)) handleClick(e, currentPage - 1);
            }}
            aria-label="Previous"
          >
            <span aria-hidden="true">{previousText}</span>
          </a>
        </li>

        {ellipses.before ? (
          <li key="ellipsisBefore" className={disabled ? 'disabled' : ''}>
            <a onClick={e => handleClick(e, ellipses.before)}>&hellip;</a>
          </li>
        ) : (
          <></>
        )}

        {pageRange.map(page => (
          <li
            key={page}
            title={showLoading ? 'Please wait...' : `Page ${page}`}
            className={
              // eslint-disable-next-line no-nested-ternary
              page === currentPage
                ? disabled
                  ? 'active disabled'
                  : 'active'
                : disabled
                ? 'disabled'
                : ''
            }
          >
            <a onClick={e => handleClick(e, page)}>
              {showLoading && page === currentPage ? (
                <Loading size="fa-sm" noPadding />
              ) : (
                page
              )}
            </a>
          </li>
        ))}

        {ellipses.after ? (
          <li key="ellipsisAfter" className={disabled ? 'disabled' : ''}>
            <a onClick={e => handleClick(e, ellipses.after)}>&hellip;</a>
          </li>
        ) : (
          <></>
        )}

        <li
          key="next"
          className={currentPage === totalPages || disabled ? 'disabled' : ''}
        >
          <a
            onClick={e => {
              if (!(currentPage === totalPages))
                handleClick(e, currentPage + 1);
            }}
            aria-label="Next"
          >
            <span aria-hidden="true">{nextText}</span>
          </a>
        </li>
      </ul>
    </nav>
  );
};

Pagination.propTypes = {
  previousText: PropTypes.string,
  nextText: PropTypes.string,
  maxSize: PropTypes.number,
  onChange: PropTypes.func,
  itemsPerPage: PropTypes.number.isRequired,
  totalItems: PropTypes.number.isRequired,
  currentPage: PropTypes.number.isRequired,
  showLoading: PropTypes.bool,
  disabled: PropTypes.bool,
};

Pagination.defaultProps = {
  previousText: '‹',
  nextText: '›',
  maxSize: 5,
  itemsPerPage: 20,
  currentPage: 1,
};

export default Pagination;
